import { type ApiError } from "@/services/intranet-portal-services";
import { type ErrorPayload } from "@/types/errors";
import { toast } from "react-toastify";

let sessionExpiryHandled = false;

const stringifyValidationError = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(stringifyValidationError).join(", ");
  }

  if (typeof value === "object" && value !== null) {
    // Handle ValidationError objects safely
    if ("message" in value && typeof (value as any).message === "string") {
      return (value as any).message;
    }

    return JSON.stringify(value);
  }

  return String(value);
};

export const isApiError = (error: unknown): error is ApiError => {
  return typeof error === "object" && error !== null && "status" in error && "body" in error;
};

export const isSessionExpired = (error: unknown): boolean => {
  if (!isApiError(error)) return false;

  if (error.status === 401) return true;

  const payload: ErrorPayload | undefined = error.body?.error;
  if (!payload) return false;

  const text = `${payload.code ?? ""} ${payload.message ?? ""}`.toLowerCase();

  return ["token", "expired", "session", "invalidtoken", "unauthorized"].some((keyword) =>
    text.includes(keyword)
  );
};

export const handleSessionExpiry = (logout: () => void): void => {
  if (sessionExpiryHandled) return;

  sessionExpiryHandled = true;

  toast.error("Your session has expired. Please log in again.", {
    toastId: "session-expired",
    autoClose: 3000,
    onClose: logout
  });
};

export const resetSessionExpiryFlag = (): void => {
  sessionExpiryHandled = false;
};

export const getApiErrorMessage = (error: unknown): string | null => {
  if (!isApiError(error)) {
    return "Unexpected error occurred";
  }

  if (isSessionExpired(error)) {
    return null;
  }

  const payload: ErrorPayload | undefined = error.body?.error;
  if (!payload) {
    return "Unable to process server response";
  }

  if (payload.validationErrors) {
    return typeof payload.validationErrors === "string"
      ? payload.validationErrors
      : Object.entries(payload.validationErrors)
          .map(([field, messages]) => {
            const message = stringifyValidationError(messages);
            return `${field}: ${message}`;
          })
          .join("; ");
  }

  if (payload.code != null && payload.code !== "") {
    const errorCodeMap: Record<string, string> = {
      "User is already inactive.": "Employee is already inactive",
      "User is already active.": "Employee is already active",
      Forbidden: "You don't have permission to perform this action",
      NotFound: "The requested resource was not found"
    };

    return errorCodeMap[payload.code] ?? payload.code;
  }

  if (
    payload.message != null &&
    payload.message !== "An internal error occurred during your request!"
  ) {
    return payload.message;
  }

  return (
    {
      400: "Invalid request",
      403: "You don't have permission to perform this action",
      404: "Resource not found",
      409: "Conflict with existing data",
      500: "Server error. Please try again later",
      503: "Service temporarily unavailable"
    }[error.status] ?? `Request failed with status ${error.status}`
  );
};
